import { concat } from '@ember/helper';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import add from 'ember-math-helpers/helpers/add';
import { eq } from 'ember-truth-helpers';
import WrButton from 'ember-wiser/components/button';
import WrDetails from 'ember-wiser/components/details';
import WrForm from 'ember-wiser/components/form';
import FileAttachementRow from 'parog-web/components/dialysis/edition-form/file-attachement-row';
import type HemodialysisMeasuredJournalEvent from 'parog-web/models/hemodialysis-measured-journal-event';
import type TypedEmberChangeset from 'parog-web/utils/typed-ember-changeset';

interface Signature {
  Args: {
    measurements: TypedEmberChangeset<HemodialysisMeasuredJournalEvent>[];
    startUltrafiltrationTotal: number | undefined;
    onSubmit: () => Promise<void>;
  };
}

interface MiddleSessionSignature {
  Args: {
    measurement: TypedEmberChangeset<HemodialysisMeasuredJournalEvent>;
    startUltrafiltrationTotal: number | undefined;
    onSubmit: () => Promise<void>;
  };
}

class MiddleSession extends Component<MiddleSessionSignature> {
  get changesetErrors() {
    const occurredAtTimeError = this.args.measurement.errors.find(
      e => e.key === 'occurredAtTime'
    );
    return occurredAtTimeError?.validation as string;
  }

  get middleUltrafiltrationRate() {
    const ultrafiltration = this.args.measurement.get('middleUltrafiltration');

    if (!ultrafiltration || !this.args.startUltrafiltrationTotal) return '-';

    return (
      (ultrafiltration / this.args.startUltrafiltrationTotal) *
      100
    ).toFixed(2);
  }

  <template>
    <WrForm @data={{@measurement}} @onSubmit={{@onSubmit}} as |form|>
      <form.InputRow
        @name='occurredAtTime'
        @label='Heure *'
        type='time'
        class={{if this.changesetErrors 'text-red border-b-red'}}
      >
        {{#if this.changesetErrors}}
          <div
            class='text-red pt-2 leading-[1.094rem] sm:pt-0'
            aria-label='occurredAtTimeError'
          >{{this.changesetErrors}}</div>
        {{/if}}
      </form.InputRow>

      <form.InputRow
        @label='Pression artérielle systolique'
        @name='middleArterialSystolicPressure'
        min='0'
        type='number'
        class='mb-2 sm:mb-0'
        placeholder='Ex: 120'
      >mmHg</form.InputRow>

      <form.InputRow
        @label='Pression artérielle diastolique'
        @name='middleArterialDiastolicPressure'
        min='0'
        type='number'
        class='mb-2 sm:mb-0'
        placeholder='Ex: 75'
      >mmHg</form.InputRow>

      <form.InputRow
        @label='Fréquence cardiaque'
        @name='middleHeartRate'
        min='0'
        type='number'
        class='mb-2 sm:mb-0'
        placeholder='Ex: 70'
      >bpm</form.InputRow>

      <form.InputRow
        @label='Ultrafiltration'
        @name='middleUltrafiltration'
        min='0'
        type='number'
        class='mb-2 sm:mb-0'
        placeholder='Ex: 200'
      >mL</form.InputRow>

      <form.Row aria-label='middleUltrafiltrationRate'>
        <:header> Taux d'ultrafiltration</:header>
        <:col1>
          {{this.middleUltrafiltrationRate}}
        </:col1>
        <:col2>
          <div class='mb-2 sm:mb-0'> %</div>
        </:col2>
      </form.Row>

      <form.InputRow
        @label='Pression artérielle machine'
        @name='middleMachineArterialPressure'
        type='number'
        class='mb-2 sm:mb-0'
        placeholder='Ex: -150'
      >mmHg</form.InputRow>

      <form.InputRow
        @label='Pression veineuse machine'
        @name='middleMachineVenousPressure'
        type='number'
        class='mb-2 sm:mb-0'
        placeholder='Ex: -150'
      >mmHg</form.InputRow>

      <FileAttachementRow
        @form={{form}}
        @title='Prendre une photo de la machine'
      />

      <form.TextareaRow
        @label='Commentaires patient'
        @name='comments'
        class='mt-2 sm:mt-0'
        placeholder='Laisser un commentaire…'
      >
        &nbsp;
      </form.TextareaRow>

      {{! Hack: add a hidden native submit button so that
        pressing "Enter" triggers a proper HTML form submission. }}
      <button type='submit' class='hidden'></button>

    </WrForm>
  </template>
}

export default class HemodialysisSessionEditionFormMiddleSessionEditionFormComponent extends Component<Signature> {
  @tracked measureNumber =
    this.args.measurements.filter(m => !m.isNew).length || 1;

  get activeMeasurements() {
    return this.args.measurements.slice(0, this.measureNumber);
  }

  addMeasure = () => {
    this.measureNumber += 1;
  };
  <template>
    {{#each this.activeMeasurements as |measurement index|}}
      <WrDetails @summary={{concat 'Mesure ' (add index 1)}} class='mt-2'>

        <MiddleSession
          @measurement={{measurement}}
          @startUltrafiltrationTotal={{@startUltrafiltrationTotal}}
          @onSubmit={{@onSubmit}}
        />

      </WrDetails>
    {{/each}}

    <div
      class='fixed bottom-[4rem] left-0 right-0 bg-[#f7f7f7] px-2 pt-2 sm:relative sm:top-[0.25em] sm:mx-0 sm:w-full sm:bg-transparent sm:p-0'
    >
      <WrButton
        {{on 'click' this.addMeasure}}
        disabled={{eq this.measureNumber 20}}
        @variant='outlined'
        class='mr-1 w-full'
        type='button'
      >
        Démarrer une nouvelle mesure
      </WrButton>
    </div>
  </template>
}
